import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { Btn, Modal } from '../ui';

// Admin "Classify every deal's industry". THE ONE-TIME BACKFILL.
//
// THE WORK RUNS ON THE SERVER AND DOES NOT NEED THIS PAGE OPEN. That is the
// whole difference between this and BulkResearchModal, whose shape it first
// copied. A research run takes minutes per deal and there is no research timer,
// so that modal genuinely IS the scheduler and closing it abandons the batch.
// NAICS has neither property: a deal is one ~4s call, and dealNaicsSweeper
// already runs hourly doing exactly this. A client-side per-deal loop therefore
// bought nothing and cost the one thing that matters — it made an unattended
// job look like one you had to babysit.
//
// So this fires ONE request at the server-side sweep and then just watches the
// remaining count fall. Two consequences worth stating:
//   • The sweep's response is IGNORED. It runs a multi-minute batch inline, well
//     past the ~45s edge timeout, so that response frequently never arrives —
//     while the invocation keeps running server-side. The dealResearch endpoint
//     has the same shape for the same reason: fire, then poll something else.
//   • Progress needs no new state. The targets endpoint already answers "how
//     many deals still need this", and that number falling IS the progress. A
//     run row would be machinery for a job measured in minutes.
//
// Close the tab whenever. Anything the batch does not reach, the hourly sweep
// picks up on its own.

const SCOPES = [
  { key: 'missing', label: 'Deals with no industry yet',    hint: 'Fills the gaps — the one-time backfill' },
  { key: 'stale',   label: 'Deals whose documents changed', hint: 'Re-reads deals that have gained documents' },
  { key: 'all',     label: 'Every deal',                    hint: 'Re-asks deals that already have a derived code — most expensive' },
];
// How often to re-count while a sweep is running. The count only moves as deals
// finish, so polling faster just adds requests.
const POLL_MS = 5000;
// A batch is bounded server-side by wall clock, so a large book may need more
// than one. Bounded here too, and the hourly sweep is the backstop either way.
const MAX_BATCHES = 12;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export const NaicsBulkModal = ({ onClose }) => {
  const [scope, setScope]     = useState('missing');
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [phase, setPhase]     = useState('idle');   // idle | running | done
  const [started, setStarted] = useState(0);        // how many needed doing when we began
  const [remaining, setRemaining] = useState(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (phase !== 'idle') return undefined;
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`deals/naics/targets?scope=${scope}`)
      .then(d => { if (!cancelled) { setTargets(d); if (d && d.available === false) setErr(d.reason || 'NAICS classification is not available yet — run migration 169.'); } })
      .catch(e => { if (!cancelled) setErr(e.message || 'Failed to load deals'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, phase]);

  useEffect(() => () => { stopRef.current = true; }, []);

  const countLeft = async () => {
    try {
      const d = await api.get(`deals/naics/targets?scope=${scope}`);
      return typeof d?.count === 'number' ? d.count : null;
    } catch { return null; }
  };

  const run = async () => {
    const total = targets?.count ?? 0;
    if (!total) return;
    stopRef.current = false;
    setPhase('running'); setStarted(total); setRemaining(total);

    for (let batch = 0; batch < MAX_BATCHES && !stopRef.current; batch++) {
      // Fired and NOT awaited for its body: this runs a multi-minute batch
      // inline and the response routinely outlives the edge. The poll below is
      // what tells us anything, and a failure here is not evidence of a failed
      // run — same arbitration the document-migration page makes.
      api.post('deals/naics/sweep', { scope, limit: 500 }).catch(() => {});

      // Watch the count fall. When it stops moving, this batch is done — either
      // finished, or out of its server-side wall clock — so start another.
      //
      // `lastLeft` is a LOCAL, not the `remaining` state: state read inside this
      // loop is captured from the render that started it and never updates, so
      // comparing against it would read every poll as "no movement" and cut the
      // batch short after fifteen seconds.
      let idle = 0;
      let lastLeft = total;
      for (;;) {
        await sleep(POLL_MS);
        if (stopRef.current) return;
        const left = await countLeft();
        if (left === null) continue;                 // transient — keep watching
        // Monotonic: a count that ticks up (a document just finished being read,
        // making its deal a target) must not make the bar go backwards.
        setRemaining(prev => (prev === null ? left : Math.min(prev, left)));
        if (left === 0) { setPhase('done'); return; }
        idle = left < lastLeft ? 0 : idle + 1;
        lastLeft = Math.min(lastLeft, left);
        if (idle >= 3) break;                        // ~15s with no movement
      }
    }
    setPhase('done');
  };

  // Closing mid-run is FINE — the server keeps going and the hourly sweep
  // finishes anything left. Nothing here needs guarding.
  const count = targets?.count ?? 0;
  const done = started && remaining !== null ? Math.max(0, started - remaining) : 0;
  const pct = started ? Math.round((done / started) * 100) : 0;

  return (
    <Modal title="Classify deal industries (NAICS)" onClose={onClose} width={560}>
      {phase === 'idle' && (
        <>
          <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14 }}>
            Each deal is read once — from the summaries already held against its documents — and
            given the NAICS industry code those documents point to. A code entered by hand is never
            touched, by any of these scopes; change one on the deal itself.
          </div>

          {SCOPES.map(sc => (
            <label key={sc.key} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px',
              border: `1px solid ${scope === sc.key ? B.navy : B.border}`, borderRadius: 8,
              marginBottom: 8, cursor: 'pointer',
            }}>
              <input type="radio" name="naics-scope" checked={scope === sc.key}
                     onChange={() => setScope(sc.key)} style={{ marginTop: 3 }} />
              <span>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{sc.label}</div>
                <div style={{ fontSize: 12, color: B.textLt }}>{sc.hint}</div>
              </span>
            </label>
          ))}

          <div style={{ fontSize: 13, color: B.textMd, margin: '12px 0' }}>
            {loading ? 'Counting…'
              : err ? <span style={{ color: B.red }}>{err}</span>
              : `${count} ${count === 1 ? 'deal' : 'deals'} to classify.`}
          </div>
          {!loading && !err && count === 0 && (
            <div style={{ fontSize: 12, color: B.textLt, marginBottom: 12 }}>
              Nothing to do. Deals with no documents read yet are not counted — they are picked
              up automatically once document intake has read them.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn disabled={loading || !!err || count === 0} onClick={run}>
              Classify {count || ''}
            </Btn>
          </div>
        </>
      )}

      {phase !== 'idle' && (
        <>
          <div style={{ fontSize: 13, color: B.textMd, marginBottom: 6 }}>
            {phase === 'running'
              ? `Classifying — ${done} of ${started} done.`
              : remaining === 0
                ? `Done — all ${started} classified.`
                : `Stopped with ${remaining} left. The hourly sweep will finish them.`}
          </div>
          {/* Stated plainly, because the previous version of this modal implied
              the opposite and people would have sat watching it. */}
          <div style={{ fontSize: 12, color: B.textLt, marginBottom: 12 }}>
            This runs on the server — you can close this page. Anything not reached is picked up
            by the hourly sweep.
          </div>
          <div style={{ height: 8, background: B.border, borderRadius: 4, overflow: 'hidden', marginBottom: 14 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: B.navy, transition: 'width 0.3s' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {phase === 'running' && (
              // Stops this page watching. It does NOT stop the server-side batch,
              // and saying otherwise would be a lie.
              <Btn variant="ghost" onClick={() => { stopRef.current = true; setPhase('done'); }}>
                Stop watching
              </Btn>
            )}
            <Btn onClick={onClose}>Close</Btn>
          </div>
        </>
      )}
    </Modal>
  );
};
